import {Action, Attributes, Card, Flag, openDialog, Separator} from 'platform/components';
import {Heading, HStack, ThemeIconKey} from 'platform/foundation';

import {toPairs} from 'ramda';

import {NotificationDefinitionResponseBody, useGetAvailableLanguagesListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri} from '@dms/routes';

import {buildArray} from 'shared/web';

import {EmailExamplePreview} from './EmailExamplePreview';
import {NotificationExamplePreview} from './NotificationExamplePreview';

interface NotificationCardProps {
  notification: NotificationDefinitionResponseBody;
}

export function NotificationCard(props: NotificationCardProps) {
  const {workspace} = getWorkspaceFromUri();
  const {data: languagesData} = useGetAvailableLanguagesListQuery({
    workspace: workspace as string,
  });

  const languageOptions = Object.entries(languagesData?.languages ?? {}).map(([value, label]) => ({
    label,
    value,
  }));

  const notificationRows = [
    {
      label: i18n.t('page.demo.notificationObligation'),
      value: props.notification.obligation,
    },
    {
      label: i18n.t('page.demo.notificationChannels'),
      content: (
        <HStack spacing={2}>
          {props.notification.channels?.map((channel) => (
            <Flag label={channel} key={channel} size="small" colorScheme="blue" isSubtle />
          ))}
        </HStack>
      ),
    },
  ];

  const recipientsSettingsRows = toPairs(props.notification.defaultRecipientSettings ?? {}).map(
    ([key, value]) => ({label: key, value})
  );

  const openNotificationPreviewDialog = () =>
    openDialog(<NotificationExamplePreview notificationId={props.notification.id} />, {
      disableBodyPadding: true,
      withAdditionalFooter: true,
      title: i18n.t('page.demo.notificationPreview'),
    });

  const openEmailPreviewDialog = (language: string) =>
    openDialog(<EmailExamplePreview notificationId={props.notification.id} language={language} />, {
      disableBodyPadding: true,
      size: 'large',
      withAdditionalFooter: true,
      title: i18n.t('page.demo.emailPreview'),
    });

  const actions = buildArray<Action>()
    .when(props.notification.channels.includes('email'), {
      type: 'dropdown-button',
      variant: 'link',
      title: i18n.t('page.demo.emailPreview'),
      menuItems: languageOptions.map((language) => ({
        label: language.label,
        value: language.value,
        leftIcon: iconValueByLanguage[language.value],
        onClick: () => openEmailPreviewDialog(language.value),
      })),
    })
    .when(props.notification.channels.includes('toastNotification'), {
      type: 'button',
      variant: 'link',
      title: i18n.t('page.demo.notificationPreview'),
      onClick: openNotificationPreviewDialog,
    });

  return (
    <Card
      title={props.notification.id}
      key={props.notification.id}
      variant={props.notification.isEnabled ? 'inlineGrey' : 'inlineWhite'}
      isExpandable
      isClosedByDefault
      actions={actions}
    >
      <Attributes size="quarter" rows={notificationRows} />
      <Separator />
      <Heading size={4}>{i18n.t('page.demo.defaultNotificationSettings')}</Heading>
      <Attributes size="quarter" rows={recipientsSettingsRows} />
    </Card>
  );
}

const iconValueByLanguage: Record<string, ThemeIconKey> = {
  cs: 'flags/CZE',
  sk: 'flags/SVK',
  de: 'flags/DEU',
  en: 'flags/GBR',
  es: 'flags/ESP',
  fr: 'flags/FRA',
  it: 'flags/ITA',
  pl: 'flags/POL',
  nl: 'flags/NLD',
};
